import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { useLocalSearchParams } from "expo-router";
import { ScrollView, View, Text } from "react-native";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { Textarea } from "@/elements/components/ui/textarea";
import { Button } from "@/elements/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import goBack from "@/elements/components/utils/go-back";
import NoShowReportedModal from "../modals/no-show-reported-modal";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import Loader from "../shared/loader";
import { Redirect } from "expo-router";

const NoShowFormSchema = z.object({
  details: z.string().optional(),
});

function StudentNoShowScreen() {
  const { lessonId } = useLocalSearchParams();
  const { data: currentUser, isSuccess: isCurrentUserSuccess } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {})
  );
  const { control, handleSubmit } = useForm({
    defaultValues: {
      details: "",
    },
    resolver: zodResolver(NoShowFormSchema),
    mode: "onChange",
  });
  const [isSuccess, setIsSuccess] = useState(false);

  const { mutateAsync: reportNoShow, isPending } = useMutation({
    mutationFn: useConvexMutation(api.tutor.noShow.mutations.reportStudentNoShow),
    onSuccess: () => {
      setIsSuccess(true);
    },
  });

  if (!isCurrentUserSuccess) {
    return <Loader />;
  }

  if (currentUser?.persona === "parent") {
    return <Redirect href="/" />;
  }

  const onClose = () => goBack();
  const onModalClose = () => goBack(true);

  const onReportNoShow = handleSubmit(async ({ details }) => {
    if (!lessonId || typeof lessonId !== 'string') return;
    
    await reportNoShow({
      sessionId: lessonId as any,
      reason: details,
    });
  });

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={onClose} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5 gap-6">
            <View className="py-2">
              <View className="gap-8">
                <View className="gap-4">
                  <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-black">
                    Report a no show
                  </Text>
                  <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-teachLoop-textPrimary">
                    We are sorry to hear that your student has not turned up to the session. Please ensure you have waited 15mins before reporting a no show.
                  </Text>
                </View>

                <Controller
                  control={control}
                  name="details"
                  render={({ field, fieldState: { error } }) => (
                    <Textarea
                      placeholder="Add any details you feel necessary."
                      className="h-[167]"
                      size="sm"
                      multiline
                      numberOfLines={4}
                      textAlignVertical="top"
                      {...field}
                      error={!!error?.message}
                    />
                  )}
                />

                <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-teachLoop-textPrimary">
                  An email will be sent to the parent and admin. You cannot undo this action.
                </Text>

                <Button
                  fullWidth
                  size="md"
                  intent="danger"
                  className="bg-red-600"
                  onPress={onReportNoShow}
                  disabled={isPending}
                >
                  <Button.Text size="md">Report no show</Button.Text>
                </Button>
              </View>
              <View className="h-3" />
            </View>
          </View>
          <View className="h-8" />
        </ResponsiveContent>
      </ScrollView>

      <NoShowReportedModal
        isVisible={isSuccess}
        onClose={() => {
          setIsSuccess(false);
          onModalClose();
        }}
        onDismiss={() => {
          setIsSuccess(false);
          onClose();
        }}
      />
    </View>
  );
}

export default StudentNoShowScreen;
